import {
  AGENCY_SECONDARY_USERS,
  CUSTOMER_COMMUNITIES,
  CUSTOMER_POSITIONS,
  CUSTOMER_SECONDARY_USERS,
  SCHEDULING_CAPACITY_PLANNING,
  SCHEDULING_DRAFTS_ASSIGNED,
  SCHEDULING_DRAFTS_UNASSIGNED,
  SCHEDULING_DRAFT_SHIFTS,
  SCHEDULING_DRAFT_SHIFTS_W_SHIFT_ID,
  SCHEDULING_DRAFT_SHIFTS_CLEAR,
  SCHEDULING_HOURS,
  SCHEDULING_JANES,
  SCHEDULING_PUBLISHED_SHIFTS_AGENCY,
  SCHEDULING_PUBLISHED_SHIFTS_INTERNAL,
  SCHEDULING_PUBLISHED_SHIFTS_OPEN,
  SCHEDULING_PUBLISHED_SHIFTS_SHARED,
  SCHEDULING_SETTINGS,
  SCHEDULING_POSITION_SETTINGS,
  SCHEDULING_SUMMARY,
  SCHEDULING_PUBLISH_SHIFTS,
  SCHEDULING_TEMPLATES,
  SCHEDULING_TEMPLATES_W_TEMPLATE_ID,
  SCHEDULING_TEMPLATES_W_TEMPLATE_ID_APPLY,
  SCHEDULING_TEMPLATES_CHECK_NAME,
  SCHEDULING_TEMPLATES_APPLIED,
  SCHEDULING_ACTIONABLE_JANES,
} from 'constants/endpoints';
import GET_schedulingDraftShiftsAssignedMapper from 'mappers/scheduling/GET_draftShiftsAssignedMapper';
import GET_schedulingDraftShiftsUnassignedMapper from 'mappers/scheduling/GET_draftShiftsUnassignedMapper';
import {
  GET_publishedShiftsAgencyMapper,
  GET_publishedShiftsInternalMapper,
  GET_publishedShiftsOpenMapper,
  GET_publishedShiftsSharedMapper,
} from 'mappers/scheduling/GET_publishedShiftsMapper';
import GET_schedulingHourMapper from 'mappers/scheduling/GET_schedulingHourMapper';
import GET_schedulingSummaryMapper from 'mappers/scheduling/GET_schedulingSummaryMapper';
import GET_customerPositionsMapper from 'mappers/scheduling/GET_customerPositionsMapper';
import { SCHEDULE_SEARCH_MEMBER_JANES_PER_PAGE } from 'pages/Schedule/PositionScheduleFiltersAndTemplatesPanel/ScheduleSearchMember/ScheduleSearchMember.constants';
import { API_NAMESPACE_SCHEDULING_SEARCH_MEMBERS } from 'constants/apiNamespaces';
import { CONFLICTS_AND_WARNINGS_TAB } from 'pages/Schedule/ScheduleTabNavigation/ConflictsAndWarningsTab';
import { SEARCHED_MEMBERS_TAB } from 'pages/Schedule/ScheduleTabNavigation/SearchedMembersTab';
import { DateTime } from 'luxon';
import { ApiRequestConfigType } from 'requests/index.types';

const toTimeZoneTime = (timestamp: string, time_zone: string) => {
  return DateTime.fromISO(timestamp).setZone(time_zone).toString();
};

export const toTimestampForTimeZone = (timestamp: string, time_zone?: string) => {
  const originalTimestamp = DateTime.fromISO(timestamp);
  const withTimeZone = originalTimestamp.setZone(time_zone).set({
    month: originalTimestamp.get('month'),
    day: originalTimestamp.get('day'),
    hour: originalTimestamp.get('hour'),
    minute: originalTimestamp.get('minute'),
    second: originalTimestamp.get('second'),
  });
  return withTimeZone.toString();
};

export const mapToDateTime = ({
  start_time,
  end_time,
  time_zone,
}: {
  start_time: string;
  end_time: string;
  time_zone?: string;
}) => {
  const startTime = DateTime.fromISO(start_time).setZone(time_zone);
  const endTime = DateTime.fromISO(end_time).setZone(time_zone);

  const start_date_time = {
    date: startTime.toFormat('yyyy-LL-dd'),
    hour: startTime.get('hour'),
    minute: startTime.get('minute'),
  };

  const end_date_time = {
    date: endTime.toFormat('yyyy-LL-dd'),
    hour: endTime.get('hour'),
    minute: endTime.get('minute'),
  };
  return [start_date_time, end_date_time];
};

const GET = 'GET';
const POST = 'POST';
const DELETE = 'DELETE';
const PATCH = 'PATCH';

export const API_GET_customerCommunitiesV2 = (): ApiRequestConfigType => ({
  method: GET,
  url: CUSTOMER_COMMUNITIES(),
  __typename: 'LegacyAPI',
});

export const API_GET_allCustomerPositionsV2 = (): ApiRequestConfigType => ({
  method: GET,
  url: CUSTOMER_POSITIONS(),
  responseTransformer: GET_customerPositionsMapper,
  __typename: 'LegacyAPI',
});

export const API_GET_customerPositionsV2 = (locationId: number): ApiRequestConfigType => ({
  method: GET,
  url: CUSTOMER_POSITIONS(),
  query: {
    filter: {
      allocated_to_communities: [locationId],
    },
  },
  responseTransformer: GET_customerPositionsMapper,
  __typename: 'LegacyAPI',
});

export const API_GET_schedulingSettings = ({
  facility_id,
}: {
  facility_id: string;
}): ApiRequestConfigType => ({
  method: GET,
  url: SCHEDULING_SETTINGS(facility_id),
  __typename: 'LegacyAPI',
});

export const API_GET_schedulingPositionSettings = ({
  facility_id,
}: {
  facility_id: string | number;
}): ApiRequestConfigType => ({
  method: GET,
  url: SCHEDULING_POSITION_SETTINGS(facility_id),
  __typename: 'LegacyAPI',
});

export const API_GET_scheduleSummary = (params: {
  facilityId: number;
  startTime: string;
  endTime: string;
  namespace?: string;
  positionCodes?: string[];
  janeIds?: string[];
  time_zone: string;
}): ApiRequestConfigType => {
  const { namespace, positionCodes, janeIds, facilityId, startTime, endTime, time_zone } = params;

  const query = {
    filter: {
      with_facility: {
        id: facilityId,
        type: 'Community',
      },
      with_positions_or_janes: {
        position_codes: positionCodes,
        jane_ids: janeIds,
      },
      with_schedule: {
        start_time: toTimeZoneTime(startTime, time_zone),
        end_time: toTimestampForTimeZone(endTime, time_zone),
      },
    },
  };

  if (positionCodes) query.filter.with_positions_or_janes.position_codes = positionCodes;
  if (janeIds) query.filter.with_positions_or_janes.jane_ids = janeIds;

  return {
    namespace,
    url: SCHEDULING_SUMMARY(),
    method: GET,
    query,
    responseTransformer: GET_schedulingSummaryMapper,
    __typename: 'LegacyAPI',
  };
};

export const API_GET_totalScheduleHours = (params: {
  positionCode: string | string[];
  facilityId: number;
  startTime: string;
  endTime: string;
  janeIds?: string[];
  namespace?: string;
  time_zone?: string;
}): ApiRequestConfigType => {
  const { positionCode, facilityId, startTime, endTime, janeIds, namespace, time_zone } = params;

  const query = {
    current_facility_id: facilityId,
    current_facility_type: 'Community',
    filter_shifts: {
      with_positions_or_janes: {
        jane_ids: janeIds,
      },
      with_schedule: {
        start_time: toTimestampForTimeZone(startTime, time_zone),
        end_time: toTimestampForTimeZone(endTime, time_zone),
      },
    },
    current_position_code: positionCode,
  };

  return {
    url: SCHEDULING_HOURS(),
    method: GET,
    query,
    responseTransformer: GET_schedulingHourMapper,
    namespace,
    __typename: 'LegacyAPI',
  };
};

export const API_GET_scheduleHours = (params: {
  positionCode: string;
  facilityId: number;
  startTime: string;
  endTime: string;
  janeIds?: string[];
  statuses?: string[];
  namespace?: string;
  employment_statuses?: string[];
  time_frames?: string[];
  group_ids?: number[];
  isShowAgencyShiftsOnly?: boolean;
  time_zone: string;
}): ApiRequestConfigType => {
  const {
    positionCode,
    facilityId,
    startTime,
    endTime,
    janeIds,
    statuses,
    namespace,
    employment_statuses,
    time_frames,
    group_ids,
    isShowAgencyShiftsOnly,
    time_zone,
  } = params;

  const query = {
    current_facility_id: facilityId,
    current_facility_type: 'Community',
    filter_shifts: {
      with_states: statuses,
      with_time_frame: time_frames,
      with_employment_statuses_or_unassigned: employment_statuses,
      with_group_ids: group_ids,
      with_positions_or_janes: {
        jane_ids: janeIds,
      },
      with_schedule: {
        start_time: toTimestampForTimeZone(startTime, time_zone),
        end_time: toTimestampForTimeZone(endTime, time_zone),
      },
      sent_to_agencies_without_internal: isShowAgencyShiftsOnly,
    },
    current_position_code: positionCode,
  };

  return {
    url: SCHEDULING_HOURS(),
    method: GET,
    query,
    responseTransformer: GET_schedulingHourMapper,
    namespace,
    __typename: 'LegacyAPI',
  };
};

export const API_GET_schedulingJanes = (params: {
  positionCode: string | string[];
  facilityId: number;
  per_page?: number;
  page?: number;
  sort_by?: string;
  jane_ids?: string[];
  employment_statuses: string[];
  group_ids: number[];
  no_pagination?: boolean;
  search_multiple_fields?: string;
  namespace?: string;
  staff: string[];
  shiftStatus?: string[];
  startTime: string;
  endTime: string;
  time_zone: string;
  timeFrames?: string[];
}): ApiRequestConfigType => {
  const {
    facilityId,
    positionCode,
    per_page,
    page,
    sort_by,
    jane_ids,
    employment_statuses,
    group_ids,
    no_pagination,
    search_multiple_fields,
    namespace,
    staff,
    shiftStatus,
    startTime,
    endTime,
    time_zone,
    timeFrames,
  } = params;

  const transformedStaff =
    staff.length > 0 ? { states: staff, community_ids: [facilityId] } : staff;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const query: any = {
    filter: {
      with_facility: {
        type: 'Community',
        id: facilityId,
      },
      with_employment_statuses: employment_statuses,
      with_staff: transformedStaff,
      ...(shiftStatus?.length
        ? {
            with_schedules_status: {
              statuses: shiftStatus,
              start_time: toTimestampForTimeZone(startTime, time_zone),
              end_time: toTimestampForTimeZone(endTime, time_zone),
            },
          }
        : {}),
      ...(timeFrames?.length
        ? {
            with_time_frame: {
              time_frames: timeFrames,
              start_time: toTimestampForTimeZone(startTime, time_zone),
              end_time: toTimestampForTimeZone(endTime, time_zone),
              include_drafts: 1,
            },
          }
        : {}),
      with_group_ids: group_ids,
      search_multiple_fields,
      with_position_code: positionCode,
      with_jane_ids: jane_ids,
    },
    ...(positionCode?.length
      ? {
          schedule_window: {
            start_time: toTimestampForTimeZone(startTime, time_zone),
            end_time: toTimestampForTimeZone(endTime, time_zone),
            community_ids: [facilityId],
            position_codes: positionCode,
          },
        }
      : {}),
    sort_by,
  };

  if (no_pagination) query.no_pagination = no_pagination;
  if (per_page) query.per_page = per_page;
  if (page) query.page = page;

  return {
    url: SCHEDULING_JANES(),
    method: GET,
    query,
    namespace,
    __typename: 'LegacyAPI',
  };
};

export const API_GET_schedulingSearchMembers = (params: {
  page: number;
  search_multiple_fields: string;
  facilityId: number;
}): ApiRequestConfigType => {
  const { page, search_multiple_fields, facilityId } = params;

  const query = {
    filter: {
      search_multiple_fields,
      with_facility: {
        id: facilityId,
        type: 'Community',
      },
    },
    per_page: SCHEDULE_SEARCH_MEMBER_JANES_PER_PAGE,
    page,
  };

  return {
    url: SCHEDULING_JANES(),
    method: GET,
    query,
    namespace: API_NAMESPACE_SCHEDULING_SEARCH_MEMBERS,
    __typename: 'LegacyAPI',
  };
};

export const API_GET_draftShiftsAssigned = (params: {
  facilityId: number;
  positionCode: string;
  startTime: string;
  endTime: string;
  time_zone: string;
  time_frames?: string[];
}): ApiRequestConfigType => {
  const { positionCode, facilityId, startTime, endTime, time_frames, time_zone } = params;
  const query = {
    include_conflicts_and_warnings: true,
    filter: {
      with_facility: {
        id: facilityId,
        type: 'Community',
      },
      with_position_code: positionCode,
      with_time_frame: time_frames,
      with_schedule: {
        start_time: toTimestampForTimeZone(startTime, time_zone),
        end_time: toTimestampForTimeZone(endTime, time_zone),
      },
    },
  };
  return {
    url: SCHEDULING_DRAFTS_ASSIGNED(),
    method: GET,
    query,
    responseTransformer: GET_schedulingDraftShiftsAssignedMapper,
    __typename: 'LegacyAPI',
  };
};

export const API_GET_draftShiftsUnassigned = (params: {
  facilityId: number;
  positionCode: string;
  startTime: string;
  endTime: string;
  time_zone?: string;
  time_frames?: string[];
}): ApiRequestConfigType => {
  const { positionCode, facilityId, startTime, endTime, time_frames, time_zone } = params;

  const query = {
    filter: {
      with_facility: {
        id: facilityId,
        type: 'Community',
      },
      with_time_frame: time_frames,
      with_position_code: positionCode,
      with_schedule: {
        start_time: toTimestampForTimeZone(startTime, time_zone),
        end_time: toTimestampForTimeZone(endTime, time_zone),
      },
    },
  };
  return {
    url: SCHEDULING_DRAFTS_UNASSIGNED(),
    method: GET,
    query,
    responseTransformer: GET_schedulingDraftShiftsUnassignedMapper,
    __typename: 'LegacyAPI',
  };
};

export type DraftShiftSkillAttributes = {
  id?: number;
  skill_id?: number;
  _destroy?: boolean;
};

export const API_POST_draftShifts = (params: {
  start_time: string;
  end_time: string;
  position_code: string;
  facility_id: number;
  facility_type: string;
  allow_overtime: boolean;
  auto_rebid?: boolean;
  sub_facility_address_id: string | null;
  unpaid_break_time: number;
  description?: string;
  jane_ids?: string[] | null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  custom_fields?: any;
  draft_shifts_skills_attributes?: DraftShiftSkillAttributes[];
  time_zone?: string;
}): ApiRequestConfigType => {
  const {
    start_time,
    end_time,
    position_code,
    facility_id,
    facility_type,
    allow_overtime,
    auto_rebid,
    unpaid_break_time,
    sub_facility_address_id,
    description,
    jane_ids: janeIds = [],
    custom_fields,
    draft_shifts_skills_attributes,
    time_zone,
  } = params;

  const [start_date_time, end_date_time] = mapToDateTime({ start_time, end_time, time_zone });

  const body = {
    draft_shift: {
      start_date_time,
      end_date_time,
      position_code,
      facility_id,
      facility_type,
      allow_overtime,
      auto_rebid,
      sub_facility_address_id,
      unpaid_break_time,
      description,
      quantity: 1,
      jane_ids: janeIds,
      custom_fields,
      draft_shifts_skills_attributes,
    },
  };
  return {
    url: SCHEDULING_DRAFT_SHIFTS(),
    method: POST,
    body,
    __typename: 'LegacyAPI',
  };
};

export const API_PATCH_draftShifts = (params: {
  id: string;
  start_time: string;
  end_time: string;
  position_code: string;
  facility_id: string | number;
  facility_type: string;
  allow_overtime: boolean;
  auto_rebid?: boolean;
  unpaid_break_time: number;
  sub_facility_address_id?: string | null;
  description: string;
  jane_ids?: string[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  custom_fields?: any;
  draft_shifts_skills_attributes?: DraftShiftSkillAttributes[];
  time_zone?: string;
}): ApiRequestConfigType => {
  const {
    id,
    start_time,
    end_time,
    position_code,
    facility_id,
    facility_type,
    allow_overtime,
    auto_rebid,
    sub_facility_address_id,
    unpaid_break_time,
    description,
    jane_ids: janeIds = [],
    custom_fields,
    draft_shifts_skills_attributes,
    time_zone,
  } = params;

  const [start_date_time, end_date_time] = mapToDateTime({ start_time, end_time, time_zone });

  const body = {
    draft_shift: {
      start_date_time,
      end_date_time,
      position_code,
      facility_id,
      facility_type,
      allow_overtime,
      auto_rebid,
      sub_facility_address_id,
      unpaid_break_time,
      description,
      quantity: 1,
      jane_ids: janeIds,
      custom_fields,
      draft_shifts_skills_attributes,
    },
  };
  return {
    url: SCHEDULING_DRAFT_SHIFTS_W_SHIFT_ID(id),
    method: PATCH,
    body,
    __typename: 'LegacyAPI',
  };
};

export const API_POST_draftShiftsLineScheduling = (params: {
  start_time: string;
  end_time: string;
  position_id: number;
  facility_id: number;
  facility_type: string;
  allow_overtime: boolean;
  auto_rebid: boolean;
  unpaid_break_time: number;
  description?: string;
  jane_ids?: string[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  custom_fields?: any;
  draft_shifts_skills_attributes?: DraftShiftSkillAttributes[];
  time_zone: string;
}): ApiRequestConfigType => {
  const {
    start_time,
    end_time,
    position_id,
    facility_id,
    facility_type,
    allow_overtime,
    auto_rebid,
    unpaid_break_time,
    description,
    jane_ids: janeIds = [],
    custom_fields,
    draft_shifts_skills_attributes,
    time_zone,
  } = params;

  const [start_date_time, end_date_time] = mapToDateTime({ start_time, end_time, time_zone });

  const body = {
    draft_shift: {
      start_date_time,
      end_date_time,
      position_id,
      facility_id,
      facility_type,
      allow_overtime,
      auto_rebid,
      unpaid_break_time,
      description,
      quantity: 1,
      jane_ids: janeIds,
      custom_fields,
      draft_shifts_skills_attributes,
    },
  };
  return {
    url: SCHEDULING_DRAFT_SHIFTS(),
    method: POST,
    body,
    __typename: 'LegacyAPI',
  };
};

export const API_PATCH_draftShiftsLineScheduling = (params: {
  id: string;
  start_time: string;
  end_time: string;
  position_id: number;
  facility_id: string;
  facility_type: string;
  allow_overtime: boolean;
  auto_rebid?: boolean;
  unpaid_break_time: number;
  description: string;
  jane_ids?: string[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  custom_fields?: any;
  draft_shifts_skills_attributes?: DraftShiftSkillAttributes[];
  time_zone: string;
}): ApiRequestConfigType => {
  const {
    id,
    start_time,
    end_time,
    position_id,
    facility_id,
    facility_type,
    allow_overtime,
    auto_rebid,
    unpaid_break_time,
    description,
    jane_ids: janeIds = [],
    custom_fields,
    draft_shifts_skills_attributes,
    time_zone,
  } = params;

  const [start_date_time, end_date_time] = mapToDateTime({ start_time, end_time, time_zone });

  const body = {
    draft_shift: {
      start_date_time,
      end_date_time,
      position_id,
      facility_id,
      facility_type,
      allow_overtime,
      auto_rebid,
      unpaid_break_time,
      description,
      quantity: 1,
      jane_ids: janeIds,
      custom_fields,
      draft_shifts_skills_attributes,
    },
  };
  return {
    url: SCHEDULING_DRAFT_SHIFTS_W_SHIFT_ID(id),
    method: PATCH,
    body,
    __typename: 'LegacyAPI',
  };
};

export const API_GET_draftShift = (shift_id: string): ApiRequestConfigType => ({
  url: SCHEDULING_DRAFT_SHIFTS_W_SHIFT_ID(shift_id),
  method: GET,
  __typename: 'LegacyAPI',
});

export const API_DELETE_draftShift = (params: { shift_id: string }): ApiRequestConfigType => {
  return {
    url: SCHEDULING_DRAFT_SHIFTS_W_SHIFT_ID(params.shift_id),
    method: DELETE,
    __typename: 'LegacyAPI',
  };
};

export const API_GET_agencySecondaryAdmins = (): ApiRequestConfigType => ({
  method: GET,
  url: AGENCY_SECONDARY_USERS(),
  __typename: 'LegacyAPI',
});

export const API_GET_customerSecondaryAdmins = (): ApiRequestConfigType => ({
  method: GET,
  url: CUSTOMER_SECONDARY_USERS(),
  __typename: 'LegacyAPI',
});

interface PublishedShiftsParams {
  facilityId: number;
  positionCode: string;
  startTime: string;
  endTime: string;
  time_zone: string;
  statuses?: string[];
  time_frames?: string[];
  isShowAgencyShiftsOnly?: boolean;
  current_position_code: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  staff: any;
}

const buildPublishedShiftsQuery = (params: PublishedShiftsParams) => {
  const {
    positionCode,
    facilityId,
    startTime,
    endTime,
    statuses,
    time_frames,
    isShowAgencyShiftsOnly,
    current_position_code,
    time_zone,
    staff,
  } = params;
  const transformedStaff = staff.length
    ? { types: staff, facility: { id: facilityId, type: 'Community' } }
    : staff;
  return {
    current_facility_id: facilityId,
    current_facility_type: 'Community',
    filter_shifts: {
      with_position_code: positionCode,
      with_schedule: {
        start_time: toTimestampForTimeZone(startTime, time_zone),
        end_time: toTimestampForTimeZone(endTime, time_zone),
      },
      with_time_frame: time_frames,
      with_states: statuses,
      with_staff: transformedStaff,
      sent_to_agencies_without_internal: isShowAgencyShiftsOnly,
    },
    current_position_code: [CONFLICTS_AND_WARNINGS_TAB, SEARCHED_MEMBERS_TAB].includes(
      current_position_code,
    )
      ? undefined
      : current_position_code,
  };
};

export const API_GET_publishedShiftsOpen = (
  params: PublishedShiftsParams,
): ApiRequestConfigType => ({
  url: SCHEDULING_PUBLISHED_SHIFTS_OPEN(),
  method: GET,
  query: buildPublishedShiftsQuery(params),
  responseTransformer: GET_publishedShiftsOpenMapper,
  __typename: 'LegacyAPI',
});

export const API_GET_publishedShiftsInternal = (
  params: PublishedShiftsParams,
): ApiRequestConfigType => ({
  url: SCHEDULING_PUBLISHED_SHIFTS_INTERNAL(),
  method: GET,
  query: buildPublishedShiftsQuery(params),
  responseTransformer: GET_publishedShiftsInternalMapper,
  __typename: 'LegacyAPI',
});

export const API_GET_publishedShiftsShared = (
  params: PublishedShiftsParams,
): ApiRequestConfigType => ({
  url: SCHEDULING_PUBLISHED_SHIFTS_SHARED(),
  method: GET,
  query: buildPublishedShiftsQuery(params),
  responseTransformer: GET_publishedShiftsSharedMapper,
  __typename: 'LegacyAPI',
});

export const API_GET_publishedShiftsAgency = (
  params: PublishedShiftsParams,
): ApiRequestConfigType => ({
  url: SCHEDULING_PUBLISHED_SHIFTS_AGENCY(),
  method: GET,
  query: buildPublishedShiftsQuery(params),
  responseTransformer: GET_publishedShiftsAgencyMapper,
  __typename: 'LegacyAPI',
});

export const API_POST_publishedShifts = (params: {
  facilityId: number;
  startTime: string;
  endTime: string;
  time_zone: string;
  positionCodes?: string[];
  janeIds?: string[];
  auto_extend_bid?: boolean;
  sub_facility_address_id?: string;
}): ApiRequestConfigType => {
  const {
    positionCodes = [],
    janeIds = [],
    facilityId,
    startTime,
    endTime,
    time_zone,
    sub_facility_address_id,
  } = params;

  const body = {
    publish: {
      with_facility: {
        id: facilityId,
        type: 'Community',
      },
      with_positions_or_janes: {
        position_codes: positionCodes,
        jane_ids: janeIds,
      },
      with_schedule: {
        start_time: toTimeZoneTime(startTime, time_zone),
        end_time: toTimestampForTimeZone(endTime, time_zone),
        sub_facility_address_id,
      },
    },
  };

  return {
    url: SCHEDULING_PUBLISH_SHIFTS(),
    method: POST,
    body,
    __typename: 'LegacyAPI',
  };
};

export const API_GET_schedulingTemplates = (params: {
  facilityId: number;
  positionCode: string;
  weekRanges?: number[];
}): ApiRequestConfigType => {
  const { facilityId, positionCode, weekRanges } = params;

  const query = {
    filter: {
      with_facility: {
        type: 'Community',
        id: facilityId,
      },
      with_number_of_weeks: weekRanges || [1, 2],
      with_position_code: positionCode,
    },
  };

  return {
    url: SCHEDULING_TEMPLATES(),
    method: GET,
    query,
    __typename: 'LegacyAPI',
  };
};

export const API_GET_schedulingTemplatesCheckName = (params: {
  name: string;
}): ApiRequestConfigType => {
  const { name } = params;

  const query = {
    name,
  };

  return {
    url: SCHEDULING_TEMPLATES_CHECK_NAME(),
    method: GET,
    query,
    __typename: 'LegacyAPI',
  };
};

export const API_GET_schedulingTemplatesApplied = (params: {
  facilityId: number;
  positionCode: string;
  startTime: string;
  endTime: string;
  time_zone: string;
}): ApiRequestConfigType => {
  const { facilityId, positionCode, startTime, endTime, time_zone } = params;

  const query = {
    filter: {
      with_facility: {
        type: 'Community',
        id: facilityId,
      },
      with_position_code: positionCode,
      with_scheduled_draft_shifts: {
        start_time: toTimestampForTimeZone(startTime, time_zone),
        end_time: toTimestampForTimeZone(endTime, time_zone),
      },
    },
  };

  return {
    url: SCHEDULING_TEMPLATES_APPLIED(),
    method: GET,
    query,
    __typename: 'LegacyAPI',
  };
};

export const API_POST_schedulingTemplate = (params: {
  name: string;
  shared?: boolean;
  facilityId: number;
  positionCode: string;
  startTime: string;
  endTime: string;
  time_zone: string;
}): ApiRequestConfigType => {
  const { name, shared, positionCode, facilityId, startTime, endTime, time_zone } = params;

  const body = {
    template: {
      name,
      shared,
    },
    shift_filters: {
      with_position_code: positionCode,
      with_facility: {
        id: facilityId,
        type: 'Community',
      },
      with_schedule: {
        start_time: toTimestampForTimeZone(startTime, time_zone),
        end_time: toTimestampForTimeZone(endTime, time_zone),
      },
    },
  };

  return {
    url: SCHEDULING_TEMPLATES(),
    method: POST,
    body,
    __typename: 'LegacyAPI',
  };
};

export const API_POST_schedulingTemplate_apply = (params: {
  templateId: number;
  startTime: string;
  endTime: string;
  time_zone: string;
}): ApiRequestConfigType => {
  const { templateId, startTime, endTime, time_zone } = params;

  const body = {
    for_schedule: {
      start_date: toTimestampForTimeZone(startTime, time_zone),
      end_date: toTimestampForTimeZone(endTime, time_zone),
    },
  };

  return {
    url: SCHEDULING_TEMPLATES_W_TEMPLATE_ID_APPLY(templateId),
    method: POST,
    body,
    __typename: 'LegacyAPI',
  };
};

export const API_PATCH_schedulingTemplate = (params: {
  id: number;
  name: string;
}): ApiRequestConfigType => {
  const { id, name } = params;

  const body = {
    template: {
      name,
    },
  };

  return {
    url: SCHEDULING_TEMPLATES_W_TEMPLATE_ID(id),
    method: PATCH,
    body,
    __typename: 'LegacyAPI',
  };
};

export const API_DELETE_schedulingTemplate = (params: {
  id: number | null;
}): ApiRequestConfigType => {
  const { id } = params;
  return {
    url: SCHEDULING_TEMPLATES_W_TEMPLATE_ID(id),
    method: DELETE,
    __typename: 'LegacyAPI',
  };
};

export const API_GET_actionableJanes = (params: {
  facilityId: number;
  namespace?: string;
  positionCodes?: string[];
  jane_ids?: string;
  startTime: string;
  endTime: string;
}): ApiRequestConfigType => {
  const { namespace, facilityId, positionCodes, jane_ids, startTime, endTime } = params;
  const query = {
    filter_draft_shift: {
      with_facility: {
        type: 'Community',
        id: facilityId,
      },
      with_schedule: {
        start_time: startTime,
        end_time: endTime,
      },
      with_positions_or_janes: {
        jane_ids,
        position_codes: positionCodes,
      },
    },
  };
  return {
    namespace,
    method: GET,
    url: SCHEDULING_ACTIONABLE_JANES(),
    query,
    __typename: 'LegacyAPI',
  };
};

export const API_DELETE_draftShiftsClear = (params: {
  facilityId: number;
  namespace?: string;
  positionCode?: string;
  startTime?: string;
  endTime?: string;
  templateId?: number | null;
}): ApiRequestConfigType => {
  const { namespace, facilityId, positionCode, startTime, endTime, templateId } = params;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const clear_draft_shifts: any = {
    with_facility: {
      id: facilityId,
      type: 'Community',
    },
  };

  if (positionCode) clear_draft_shifts.with_position_code = [positionCode];

  if (startTime && endTime) {
    clear_draft_shifts.with_schedule = {
      start_time: startTime,
      end_time: endTime,
    };
  }

  if (templateId != null && templateId >= 0) {
    clear_draft_shifts.from_template = [templateId];
  }

  return {
    namespace,
    url: SCHEDULING_DRAFT_SHIFTS_CLEAR(),
    method: DELETE,
    body: { clear_draft_shifts },
    __typename: 'LegacyAPI',
  };
};
export const API_GET_schedulingCapacityPlanning = ({
  position_id,
  facilityId,
  start_time,
  end_time,
}: {
  position_id: string;
  facilityId: number;
  start_time: string;
  end_time: string;
}): ApiRequestConfigType => {
  const query = {
    community_id: facilityId,
    position_id,
    with_schedule: {
      start_time,
      end_time,
    },
  };

  return {
    url: SCHEDULING_CAPACITY_PLANNING(),
    method: GET,
    query,
    __typename: 'LegacyAPI',
  };
};
